import {useIssueCreateMetadata} from '@github-ui/issue-create/IssueCreateDataContext'
import type {RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import type {UseQueryResult} from '@github-ui/react-query'
import {useCallback, useEffect, useMemo, useRef} from 'react'

import type {DraftIssue} from '../content-preview-types'

/**
 * Configuration options for synchronizing draft issue data with form state.
 *
 * @template TFormValue - The type of the form value from `IssueCreateDataContext`
 * @template TDraftValue - The type of the draft value from `DraftIssue`
 */
interface UseDraftIssueFormSyncOptions<TFormValue, TDraftValue> {
  /** The current draft value extracted from the draft issue preview object */
  draftValue: TDraftValue
  /** The current form value in the UI context */
  formValue: TFormValue
  /** Setter to update the form value in the UI context */
  setFormValue: (value: TFormValue) => void
  /**
   * Callback fired when the form value changes. This should call `useOnChangeCallback`.
   * @param newValue - The new form value
   * @param skipVersionBump - Whether to skip incrementing the draft version
   */
  onChange: (newValue: TFormValue, skipVersionBump: boolean) => void
  /**
   * Partial result of `useQuery` hook which resolves Copilot's text values into form values.
   */
  resolverResult: Pick<UseQueryResult<TFormValue>, 'isLoading' | 'data'>
  /**
   * Function to check if the user has permission to modify the specific metadata field.
   */
  hasPermission: (repository: RepositoryPickerRepository | undefined) => boolean | undefined
  /**
   * Function to determine if two form values are equal.
   */
  isEqual: (a: TFormValue | undefined, b: TFormValue | undefined) => boolean
  /**
   * The default/initial value to use as a fallback.
   */
  defaultValue: TFormValue
}

/**
 * A React hook that synchronizes draft issue data with form state, handling the
 * coordination between streaming draft data and form values.
 *
 * This hook manages the synchronization lifecycle when:
 * - Opening a preview from streaming draft data
 * - Opening a preview from completed/streamed blocks
 * - Switching between different drafts
 * - Modifying form values by the user
 *
 * The hook ensures that form values are properly resolved from draft data while respecting
 * user permissions and preventing race conditions that could lead to infinite update loops.
 */
export function useDraftIssueFormSync<TFormValue, TDraftValue>(
  draftIssue: DraftIssue,
  options: UseDraftIssueFormSyncOptions<TFormValue, TDraftValue>,
) {
  const {draftValue, formValue, setFormValue, onChange, resolverResult, hasPermission, isEqual, defaultValue} = options
  const {repository} = useIssueCreateMetadata()

  const isResolvingFormValue = useRef(true)
  const lastKnownFormValue = useRef<TFormValue | undefined>(undefined)
  const lastKnownDraft = useRef<`${DraftIssue['id']}`>(draftIssue.id)

  // Reset on switching a draft
  useEffect(() => {
    if (lastKnownDraft.current !== draftIssue.id) {
      isResolvingFormValue.current = true
      lastKnownFormValue.current = undefined
      lastKnownDraft.current = draftIssue.id
    }
  }, [draftIssue.id, formValue])

  // When switching drafts with different repositories,
  // we need to make sure the repository from context matches the draft's data
  const hasMatchingRepo = useMemo(() => {
    return draftIssue.repository && repository?.nameWithOwner === draftIssue.repository
  }, [draftIssue.repository, repository?.nameWithOwner])

  const {isLoading: loadingResolvedValue, data: resolvedValue} = resolverResult

  const isValueResolved = useMemo(() => {
    // Only process changes if data is streamed,
    // this is to prevent racing condition that leads to infinite loop
    const isValueStreamed = draftValue != null || !draftIssue.isStreaming
    return isValueStreamed && !loadingResolvedValue
  }, [draftValue, draftIssue.isStreaming, loadingResolvedValue])

  // Update resolving state
  useEffect(() => {
    if (hasMatchingRepo && !isValueResolved) {
      isResolvingFormValue.current = true
    }
  }, [hasMatchingRepo, isValueResolved])

  const onValueChange = useCallback(
    (newValue: TFormValue, skipVersionBump: boolean) => {
      if (lastKnownFormValue.current === undefined || !isEqual(lastKnownFormValue.current, newValue)) {
        onChange(newValue, skipVersionBump)
        lastKnownFormValue.current = newValue
      }
    },
    [isEqual, onChange],
  )

  // Updates from resolving draftIssue.[draftFieldName]. It only runs after:
  // - Opening a preview from streaming
  // - Opening a preview from streamed block
  // - Switching to a different draft
  useEffect(() => {
    if (!hasMatchingRepo || !isValueResolved) {
      return
    }

    if (hasPermission(repository)) {
      // Since we checked `isValueResolved`, we know that `resolvedValue` is defined
      setFormValue(resolvedValue as TFormValue)
      onValueChange(resolvedValue as TFormValue, true)
    } else if (repository != null) {
      setFormValue(defaultValue)
      onValueChange(defaultValue, true)
    }
  }, [
    hasMatchingRepo,
    isValueResolved,
    onValueChange,
    repository,
    resolvedValue,
    setFormValue,
    hasPermission,
    defaultValue,
  ])

  // Update resolving state
  useEffect(() => {
    if (
      !isResolvingFormValue.current ||
      lastKnownFormValue.current === undefined ||
      !hasMatchingRepo ||
      !isValueResolved ||
      !repository
    ) {
      return
    }

    const viewerHasPermission = hasPermission(repository)
    if (viewerHasPermission && isEqual(formValue, resolvedValue)) {
      isResolvingFormValue.current = false
    } else if (!viewerHasPermission && isEqual(formValue, defaultValue)) {
      isResolvingFormValue.current = false
    }
  }, [
    draftIssue.id,
    hasMatchingRepo,
    isValueResolved,
    resolvedValue,
    repository,
    formValue,
    hasPermission,
    defaultValue,
    isEqual,
  ])

  // Process form changes if not resolving from draftIssue data
  useEffect(() => {
    if (!isResolvingFormValue.current) {
      onValueChange(formValue, false)
    }
  }, [formValue, onValueChange])
}
